import { CircularProgress } from "@mui/material";
import { useState } from "react";
import { checkTransactionConfirmationReceipt } from "../../utils/methods";
import { formatValue } from "../../utils/formatValue";
import { getContract, getMetaMaskSigner } from "../../services/contract.service";
import { describeChainError } from "../../services/challenge.service";
import { isUserRejection } from "../../services/relay.service";
import { LandToken } from "../../types/LandToken";
import LandActionPopup, { PopupCTA, PopupFootnote } from "../LandPage/LandActionPopup";

type OfferCancellationModalProps = {
  token: LandToken;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
};

// The Guardian Portal's cancel-listing popup, on the land-card + purple-panel shell and in the
// same shape as the land page's copy (LandPage/index.tsx, "Cancel Offer Confirmation Popup"):
// the standing high offer as the heading and one CTA. No detail rows by design.
const OfferCancellationModal = ({ token, open, setOpen, onSuccess }: OfferCancellationModalProps) => {
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    // A cancel in flight can't be dismissed — the wallet prompt is still up.
    if (loading) {
      return;
    }
    setOpen(false);
    setMessage("");
  };

  // The portal's token rows carry their offers inline (`token.offers`), unlike the land page,
  // which fetches them separately — same "highest amount wins" pick as its fetchHighestOffer.
  const offers: any[] = Array.isArray((token as any)?.offers) ? (token as any).offers : [];
  const highest = offers.length
    ? offers.reduce((max: any, offer: any) => (Number(offer.amount) > Number(max.amount) ? offer : max), offers[0])
    : null;
  const highestOffer: number | null = highest ? Number(highest.amount) : null;
  const highestOfferBuyer: string | null = highest?.buyer ?? null;

  const handleCancelOffer = async () => {
    setLoading(true);
    setMessage("");
    try {
      const signer = await getMetaMaskSigner();
      const NFTContract = getContract("ecofutures-token", signer);
      const tokenId = Number(token?.etNft?.id);

      if (NFTContract && tokenId !== null && tokenId !== undefined) {
        const result = await NFTContract.cancelListing(tokenId);
        const r = await checkTransactionConfirmationReceipt(result.hash);
        const receipt = r as any;
        // The raw JSON-RPC receipt carries `status` as "0x0" / "0x1" (a decoded one as 0 / 1),
        // so the old `!== 0` compare treated a reverted transaction as a success.
        if (Number(receipt?.status) !== 0) {
          setOpen(false);
          if (onSuccess) {
            onSuccess();
          }
        } else {
          setMessage("The cancel transaction was mined but reverted.");
        }
      } else {
        setMessage("Contract not found or token ID is invalid");
      }
    } catch (error: any) {
      console.error("Error cancelling offer:", error);
      // Names the V10.1 revert (NoActiveListing, OnlyOwnerCanCancelListing) rather than showing
      // its selector; a wallet rejection gets its own line.
      setMessage(isUserRejection(error) ? "Cancelled in your wallet — nothing was sent." : describeChainError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LandActionPopup
      open={open}
      onClose={handleClose}
      token={token}
      statLabel="HIGHEST OFFER"
      statValue={highestOffer !== null ? `$${formatValue(highestOffer, true, 2)}` : "N/A"}
      statAddress={highestOfferBuyer}
    >
      <PopupCTA disabled={loading} onClick={handleCancelOffer} variant="contained">
        {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Cancel listing"}
      </PopupCTA>
      {/* The panel has no field to carry an error ring, so the one line under the CTA is where
          a revert or a wallet rejection shows. */}
      {message && <PopupFootnote tone="error">{message}</PopupFootnote>}
    </LandActionPopup>
  );
};

export default OfferCancellationModal;
